import { useRef } from "react";
import { Link } from "react-router-dom";
import { m as motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight, Star, ShieldCheck } from "lucide-react";
import { IMG, SITE } from "@/data/site";
import { Img, MaskLines, Planks } from "../motion";
import { Btn, PhoneLink } from "../ui-bits";

const EASE = [0.16, 1, 0.3, 1];

const Mark = ({ children }) => (
  <span className="relative inline-block whitespace-nowrap">
    <motion.span
      aria-hidden="true"
      className="absolute inset-x-[-0.06em] bottom-[0.06em] top-[0.18em] -z-10 origin-left -skew-x-12 bg-[#F2C200]"
      initial={{ scaleX: 0 }}
      animate={{ scaleX: 1 }}
      transition={{ duration: 0.9, delay: 0.75, ease: EASE }}
    />
    {children}
  </span>
);

const PILLARS = [
  { n: "01", t: "Wholesale Pricing", d: "Quality flooring without traditional retail markups.", to: "/flooring/luxury-vinyl-plank" },
  { n: "02", t: "Professional Installation", d: "Buy your floor and have it installed by one company.", to: "/installation" },
  { n: "03", t: "Contractor Distribution", d: "Volume pricing and jobsite delivery for the trade.", to: "/contractors" },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [5, -5]), { stiffness: 110, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-7, 7]), { stiffness: 110, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <section ref={ref} className="relative overflow-hidden bg-white" aria-label="Wholesale flooring savings" data-testid="home-hero">
      <div className="pointer-events-none absolute inset-y-0 right-0 hidden w-[38%] bg-[#f6f4ef] lg:block" aria-hidden="true" />
      <div className="wrap relative grid items-center gap-10 pb-12 pt-8 sm:pt-12 lg:grid-cols-12 lg:gap-12 lg:pb-16 lg:pt-14">
        <div className="lg:col-span-7">
          <motion.p className="eyebrow text-[#5a4a33]" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }}>
            Cornelius, NC · Lake Norman &amp; Greater Charlotte
          </motion.p>
          <MaskLines
            testId="hero-title"
            className="relative z-0 mt-5 text-[2.55rem] font-extrabold leading-[0.98] tracking-[-0.045em] text-[#111] sm:text-6xl lg:text-[3.9rem] xl:text-[4.4rem]"
            lines={["Quality Flooring", <>for <Mark>40–50% Less</Mark></>, "Than Big Box", "Store Pricing"]}
          />
          <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 1, delay: 0.6, ease: EASE }}>
            <Planks className="mt-6 h-4 w-28" />
          </motion.div>
          <motion.div initial={{ y: 16 }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.3, ease: EASE }}>
            <p className="mt-6 text-lg font-extrabold tracking-tight text-[#111] md:text-xl">Wholesale Flooring. Professional Installation. Serious Savings.</p>
            <p className="mt-3 max-w-xl text-base leading-relaxed text-[#444] md:text-lg">
              Shop wholesale luxury vinyl plank with professional installation available throughout Huntersville, Cornelius, Denver, Sherrills Ford, Iron Station, and surrounding Lake Norman communities.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <Btn to="/free-estimate" size="lg" testId="hero-get-estimate-btn">Get a Free Flooring Estimate</Btn>
              <Btn to="/flooring/luxury-vinyl-plank" variant="ink" size="lg" arrow={false} testId="hero-shop-flooring-btn">Shop LVP</Btn>
              <Btn to="/contractor-pricing" variant="outline" size="lg" arrow={false} testId="hero-contractor-pricing-btn">Contractor &amp; Builder Pricing</Btn>
            </div>
            <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm">
              <a href={SITE.mapsUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 font-semibold" data-testid="hero-google-rating">
                <span className="inline-flex">{[0, 1, 2, 3, 4].map((i) => <Star key={i} className="h-4 w-4 fill-[#F2C200] text-[#F2C200]" />)}</span>
                {SITE.rating.value} on Google ({SITE.rating.count} reviews)
              </a>
              <PhoneLink testId="hero-phone-link" className="inline-flex items-center gap-2 font-extrabold" />
            </div>
          </motion.div>
        </div>

        <div className="relative lg:col-span-5" style={{ perspective: 1200 }} onMouseMove={onMove} onMouseLeave={reset}>
          <motion.div
            style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.2, ease: EASE }}
            className="relative aspect-[4/3] sm:aspect-[16/11] lg:aspect-[4/5]"
          >
            <div className="plank-clip h-full overflow-hidden rounded-[22px] bg-[#e9e4d9]">
              <motion.div style={{ y }} className="h-[118%] w-full -translate-y-[8%]">
                <Img id={IMG.hero} alt="Professionally installed wide-plank wood-look flooring in an upscale Lake Norman living room" priority w={1200} sizes="(min-width:1024px) 40vw, 100vw" className="h-full w-full object-cover" />
              </motion.div>
            </div>
            <div className="absolute -bottom-5 left-4 rounded-2xl bg-[#111] p-5 text-white shadow-2xl sm:-left-6 sm:p-6" style={{ transform: "translateZ(60px)" }} data-testid="hero-savings-badge">
              <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#F2C200]">You save</p>
              <p className="mt-1 text-4xl font-extrabold tracking-tight sm:text-5xl">40–50%</p>
              <p className="text-sm text-zinc-300">vs. big-box store pricing</p>
            </div>
            <div className="absolute -right-2 top-6 hidden items-center gap-2 rounded-full bg-[#F2C200] px-4 py-2 text-sm font-bold shadow-xl sm:flex" style={{ transform: "translateZ(40px)" }}>
              <ShieldCheck className="h-4 w-4" /> Installation available
            </div>
          </motion.div>
        </div>
      </div>

      <div className="wrap relative pb-4">
        <ul className="grid border-t border-[#111] md:grid-cols-3">
          {PILLARS.map((p, i) => (
            <motion.li key={p.n} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.9 + i * 0.1, ease: EASE }} className={`border-b border-[#e7e3da] md:border-b-0 ${i > 0 ? "md:border-l md:border-[#e7e3da]" : ""}`}>
              <Link to={p.to} className="group flex items-start gap-4 py-6 md:px-6 md:first:pl-0" data-testid={`hero-pillar-${p.n}`}>
                <span className="font-mono text-sm text-[#8a6440]">{p.n}</span>
                <span className="flex-1">
                  <span className="block text-lg font-extrabold">{p.t}</span>
                  <span className="mt-1 block text-[15px] text-[#555]">{p.d}</span>
                </span>
                <ArrowRight className="mt-1 h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </motion.li>
          ))}
        </ul>
      </div>
    </section>
  );
};

const WORDS = ["Wholesale LVP", "Waterproof Luxury Vinyl Plank", "Professional Installation", "Jobsite Delivery", "Huntersville", "Cornelius", "Denver", "Sherrills Ford", "Iron Station", "Contractor Pricing"];

export const Marquee = () => (
  <div className="overflow-hidden bg-[#111] py-5 text-white" aria-hidden="true" data-testid="home-marquee">
    <div className="marquee-track flex w-max items-center gap-10 whitespace-nowrap">
      {[...WORDS, ...WORDS].map((w, i) => (
        <span key={i} className="flex items-center gap-10 text-2xl font-extrabold tracking-tight sm:text-3xl">
          <span className={i % 2 ? "text-white" : "text-[#F2C200]"}>{w}</span>
          <Planks className="h-3 w-12" />
        </span>
      ))}
    </div>
  </div>
);
